import React from 'react';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import classNames from 'classnames';
import { ExternalLink, LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;
  const project = options?.projects;
  const onToggleFavorite = options?.onToggleFavorite;
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';
  const isFeaturedListing = options?.isFeaturedListing;
  // use block builder instead of mapping blocks manually

  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      projects={project}
      onToggleFavorite={onToggleFavorite}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      isFeaturedListing={isFeaturedListing}
    >
      <div className={css.footer}>
        <div
          className={classNames(css.content, getContentCss(numberOfColumns))}
        >
          <div className={css.logoContainer}>
            {/* Same-tab: this is an internal link back to the home page, not an external
                destination. target="_blank" here spawned a duplicate tab of the same site. */}
            <a href="/" aria-label="GFHR home">
              <img src="/static/img/footer_logo.svg" alt="GFHR" />
            </a>

            {/* <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            /> */}
          </div>
          <div className={css.sloganMobile}>
            <Field data={slogan} className={css.slogan} />
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              <Field data={slogan} className={css.slogan} />
            </div>
            {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder
                  blocks={linksWithBlockId}
                  sectionId={sectionId}
                  options={options}
                />
              </div>
            ) : null}
            <Field data={copyright} className={css.copyright} />
          </div>
          <div
            className={`${css.spacing} ${classNames(
              css.grid,
              getGridCss(numberOfColumns)
            )}`}
          >
            {/* <BlockBuilder
              blocks={blocks}
              sectionId={sectionId}
              options={options}
            /> */}
            {blocks?.map(block => (
              <div key={block.blockId}>
                {block.blockId === 'general' && (
                  <div className={css.generalContent}>
                    <Field data={block.text} />
                    {/* <div className={css.communityLink}>
                      <a
                        href="https://community.gfhr.co/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Community
                      </a>
                    </div> */}
                  </div>
                )}
                {block.blockId === 'general-2' && (
                  <div className={css.generalContent}>
                    <Field data={block.text} />
                  </div>
                )}
                {block.blockId === 'terms-and-privacy' && (
                  <div className={css.generalContent}>
                    <Field data={block.text} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
